import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AttorneySelect({ value, onChange, label = 'Attorney', required = false }) {
  const [attorneys, setAttorneys] = useState([]);
  const [search, setSearch] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/api/attorneys`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setAttorneys(res.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(false));
  }, []);

  // Sync external value changes
  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = attorneys.filter((a) => {
    const q = search.toLowerCase();
    return (
      (a.full_name || '').toLowerCase().includes(q) ||
      (a.firm_name || '').toLowerCase().includes(q)
    );
  });

  const handleSelect = (atty) => {
    setSearch(atty.full_name || '');
    setOpen(false);
    if (onChange) {
      onChange(atty.id, atty.full_name, atty.firm_name, atty.phone, atty.email, atty);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setOpen(true);
    // If user clears or types custom text, pass it through as manual entry
    if (onChange) {
      onChange(null, val, '', '', '', null);
    }
  };

  // If attorneys didn't load, fall back to plain text input
  if (!loaded || attorneys.length === 0) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (onChange) onChange(null, e.target.value, '', '', '', null);
          }}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          required={required}
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={search}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        placeholder="Type to search attorneys..."
        required={required}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
          {filtered.slice(0, 8).map((a) => (
            <div
              key={a.id}
              onClick={() => handleSelect(a)}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{a.full_name}</span>
              {a.firm_name && <span className="text-gray-500"> - {a.firm_name}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
